import { Box, Card, Grid } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  VendorRequestDTO,
  VendorContactDTO,
  getVendorContactsListNew,
  getVendorContactById,
} from 'wisestep-api';
import {
  ControlledTextInput,
  getDateUsingFormat,
  WsButton,
  WsColors,
  WsDrawer,
  WsIconMoreVer,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';
import { getCommonGridStyles } from '../../../features/clientListingPage/helper/ClientListingHelper';
import AddEntityButton from '../../clientDetails/components/addEntityButton/AddEntityButton';
import { CustomToolbar } from '../../invoicing/components/DataGridCustomToolBar';

import VendorContactForm from './VendorContactForm';

interface IVendorContactsList {
  vendorId: string;
}

const VendorContactsList = ({ vendorId }: IVendorContactsList) => {
  const methods = useForm();
  const { handleSubmit, reset } = methods;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const tableData: Record<string, unknown>[] = [];
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'firstName', sort: 'asc' }]);
  const [vendorContactListData, setVendorContactListData] = useState<VendorContactDTO[]>([]);
  const [showAddContactDialog, setShowAddContactDialog] = useState<boolean>(false);
  const [vendorContact, setVendorContact] = useState<VendorContactDTO | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Contact Name',
      flex: 0.8,
      renderCell: (params) => {
        return (
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            bold
            color={WsTypoColors.BLACK}
            cursor="pointer"
          >
            {params.row['firstName']} {params.row['lastName']}
          </WsTypography>
        );
      },
    },
    {
      field: 'active',
      headerName: 'Status',
      flex: 0.8,
      renderCell: (params) => {
        return (
          <WsTypography variant={WsTypoVariants.BODY1}>
            {params.row['active'] ? 'Active' : 'Inactive'}
          </WsTypography>
        );
      },
    },
    {
      field: 'position',
      headerName: 'Designation',
      flex: 0.8,
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 0.8,
    },
    {
      field: 'phoneNumber',
      headerName: 'Contact',
      flex: 0.8,
    },
    {
      field: 'emailAddress',
      headerName: 'Email',
      flex: 0.8,
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 0.8,
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 0.8,
    },
    {
      field: 'edit',
      headerName: '',
      flex: 0.2,
      sortable: false,
      renderCell: (params) => (
        <PopoverOnClick element={<WsIconMoreVer />}>
          <Box p={1}>
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              cursor="pointer"
              onClick={() => fetchVendorContactById(params.id)}
            >
              Edit
            </WsTypography>
          </Box>
        </PopoverOnClick>
      ),
    },
  ];

  vendorContactListData.forEach((vendorData) => {
    tableData.push({
      firstName: vendorData.firstName,
      emailAddress: vendorData.emailAddress,
      position: vendorData.position,
      phoneNumber: vendorData.phoneNumber,
      location: vendorData.locationName,
      active: vendorData.active,
      id: vendorData.id,
      createdOn:
        vendorData?.createdOn != undefined
          ? getDateUsingFormat(vendorData.createdOn?.toString(), 'DD-MMM-YY')
          : '',
      createdBy: vendorData.createdByName,
    });
  });

  const clearFilter = () => {
    reset();
    methods.setValue('query', '');
    setSearchQuery('');
  };

  const onAddButtonClick = () => {
    reset();
    setVendorContact(undefined);
    setShowAddContactDialog(true);
  };

  const fetchVendorContactById = async (contactId) => {
    setShowAddContactDialog(true);
    setIsLoading(true);
    try {
      const response = await getVendorContactById(contactId);
      setVendorContact(response.data);
    } catch (err) {
      console.log(err);
    }
    setIsLoading(false);
  };

  const onSearch = (data) => setSearchQuery(data?.query ?? '');

  const getVendorContactDataList = useCallback(
    async (status?: string[]) => {
      try {
        const searchText = searchQuery ?? '';
        const query: string = searchText ?? '';
        const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
        const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
        const vendorReqDTO: VendorRequestDTO = {
          status: status ?? [],
          query: query,
          pageNo: pageNo,
          pageSize: pageSize,
          sortBy: sortBy ?? '',
          sortOrder: sortOrder ?? '',
          vendorIds: vendorId ? [vendorId] : [],
        };
        const response = await getVendorContactsListNew(vendorReqDTO);
        setTotalRecord(response.data.totalElements);
        setVendorContactListData(response.data.content);
      } catch (err) {
        console.log(err);
      }
    },
    [pageNo, pageSize, searchQuery, sortModel, vendorId]
  );

  useEffect(() => {
    getVendorContactDataList();
  }, [getVendorContactDataList]);

  return (
    <>
      <Grid item xs={12} spacing={2} mt={1}>
        <>
          <Grid item container xs={12} justifyContent="flex-end" alignItems="center">
            <Grid item container xs={12} justifyContent="flex-end">
              <AddEntityButton
                onClick={onAddButtonClick}
                justifyContent="flex-end"
                buttonText="Add Contact"
              />
            </Grid>
          </Grid>
          <Grid
            sx={{ backgroundColor: WsColors.TheBlues50 }}
            item
            xs={12}
            justifyContent="flex-end"
            pt={1}
          >
            <FormProvider {...methods}>
              <Grid
                container
                sx={{ display: 'flex', backgroundColor: WsColors.TheBlues50 }}
                mb={2}
                component="form"
                onSubmit={handleSubmit(onSearch)}
              >
                <Grid item xs={10} p={1}>
                  <Card elevation={0} sx={{ p: 1 }}>
                    <ControlledTextInput
                      fullWidth
                      name="query"
                      placeholder="Search for Name"
                      size="small"
                    />
                  </Card>
                </Grid>
                <Grid item xs={1} mt={3} pr={2}>
                  <WsButton
                    fullWidth
                    variant="text"
                    size="large"
                    type="reset"
                    onClick={clearFilter}
                  >
                    Reset
                  </WsButton>
                </Grid>
                <Grid item xs={1} mt={3} pr={2}>
                  <WsButton fullWidth size="large" type="submit">
                    Search
                  </WsButton>
                </Grid>
              </Grid>
            </FormProvider>
          </Grid>
        </>
        <Grid xs={12} bgcolor={WsColors.White} mt={1}>
          <DataGrid
            autoHeight={true}
            components={{ Toolbar: CustomToolbar }}
            columns={columns}
            rows={tableData}
            onPageChange={(page) => setPageNo(page)}
            page={pageNo}
            paginationMode="server"
            pageSize={pageSize}
            rowCount={totalRecord}
            rowsPerPageOptions={[25, 50, 75, 100]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => {
              setPageNo(0);
              setSortModel(newSortModel);
            }}
            sortingMode="server"
            getRowHeight={() => 'auto'}
            sx={getCommonGridStyles()}
          />
        </Grid>
      </Grid>
      {showAddContactDialog && !isLoading && (
        <WsDrawer
          size="medium"
          open={showAddContactDialog}
          onClose={() => setShowAddContactDialog(false)}
          title={vendorContact ? 'Update Vendor Contacts' : 'Add Vendor Contacts'}
        >
          <VendorContactForm
            vendorId={vendorId}
            vendorContact={vendorContact}
            onClose={() => {
              setShowAddContactDialog(false);
              getVendorContactDataList();
            }}
          />
        </WsDrawer>
      )}
    </>
  );
};

export default VendorContactsList;
